/**
 * Sends a /ga/session request to the old express backend which
 * will create session info and store it in the redis database
 * so that it can push a purchase event to GA4 when it receives
 * a webhook from Centra.
 */
import { FrontendContextV1 } from "@made-people/nestjs-bff-service";
import { createMmBackendClient } from "./create-mm-backend-client";
import Cookies from "js-cookie";

// Production GA4 ID
const GA4_ID = "G-3PPYTRJV8E";

const gtagGet = (target: string, field: string, timeoutMs = 500) =>
  new Promise<string | undefined>((resolve) => {
    if (typeof window === "undefined") {
      resolve(undefined);
      return;
    }

    const theWindow = window as Window & {
      gtag?: (
        command: string,
        target: string,
        field: string,
        callback: (value: string | undefined) => void,
      ) => void;
    };

    if (typeof theWindow.gtag !== "function") {
      resolve(undefined);
      return;
    }

    let settled = false;

    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(undefined);
    }, timeoutMs);

    try {
      theWindow.gtag("get", target, field, (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        resolve(value);
      });
    } catch {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve(undefined);
    }
  });

const sleep = (ms: number) =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

async function gtagGetWithRetry(
  target: string,
  field: string,
  attempts = 5,
  delayMs = 500,
) {
  for (let attempt = 1; attempt <= attempts; attempt++) {
    const value = await gtagGet(target, field, delayMs);

    if (value !== undefined && value !== "") {
      return value;
    }

    if (attempt < attempts) {
      await sleep(delayMs);
    }
  }

  return undefined;
}

function getTrackingInfo({ url, headers }: { url: URL; headers: Headers }) {
  const query = Object.fromEntries(url.searchParams.entries());
  const isLandingPage =
    Object.keys(query).find(
      (key) => key === "gclid" || key.startsWith("utm_"),
    ) !== undefined;

  let landingPageUrl;
  if (isLandingPage) {
    landingPageUrl = new URL(`${url.origin}${url.pathname}`);
  }
  return {
    url: landingPageUrl?.toString(),
    headers: Object.fromEntries(headers.entries()),
  };
}

export function gaCreateSession(
  token: string,
  { url, headers }: { url: URL; headers: Headers },
  frontendContext: FrontendContextV1,
) {
  if (typeof window === "undefined") {
    return;
  }

  // Set up client for backend API that also adds request context stuff
  const backendClient = createMmBackendClient(frontendContext);

  async function sendToServer() {
    const [sessionId, clientID] = await Promise.all([
      gtagGetWithRetry(GA4_ID, "session_id"),
      gtagGetWithRetry(GA4_ID, "client_id"),
    ]);
    const trackingInfo = getTrackingInfo({ url, headers }) ?? {};

    const gbuuid = Cookies.get("gbuuid");

    // Set trackingInfo in backend
    await backendClient
      .put(`/ga/session`, {
        ...trackingInfo,
        gaClientId: clientID,
        gaSessionId: sessionId,
        selectionId: token,
        gbuuid,
      })
      .catch((error) => {
        console.error("GA: Failed to set trackingInfo", error);
      });
  }

  sendToServer();
}
